import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, Pencil, Plus, Search, Trash2, Undo2, X } from 'lucide-react';
import {
  CUSTOM_FIELD_TYPE_LABELS,
  CUSTOM_FIELD_TYPES,
  CustomField,
  CustomFieldsApi,
  CustomFieldType,
  OPTION_TYPES,
} from '@/services/custom-fields.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';
import type { PaginatedResult } from '@/components/data-table';

const columnHelper = createColumnHelper<CustomField>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

function asStaticPage<T>(items: T[]): PaginatedResult<T> {
  return { items, total: items.length, page: 1, limit: Math.max(items.length, 1), totalPages: 1 };
}

interface FormState {
  id: string | null;
  name: string;
  type: CustomFieldType;
  isRequired: boolean;
  options: string[];
}

export function CustomFieldsPage() {
  const dt = useDataTable({ defaultLimit: 100 });
  const [showDeleted, setShowDeleted] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | undefined>();
  const [deleting, setDeleting] = useState<CustomField | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const qc = useQueryClient();

  const { data: fields, isFetching, error } = useQuery({
    queryKey: ['custom-fields', { search: dt.params.search, showDeleted }],
    queryFn: () => CustomFieldsApi.list({ search: dt.params.search, showDeleted }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['custom-fields'] });

  const save = useMutation({
    mutationFn: () => {
      const name = form!.name.trim();
      const options = OPTION_TYPES.has(form!.type) ? form!.options.map((o) => o.trim()).filter(Boolean) : undefined;
      return form!.id
        ? CustomFieldsApi.update(form!.id, { name, isRequired: form!.isRequired, options })
        : CustomFieldsApi.create({ name, type: form!.type, isRequired: form!.isRequired, options });
    },
    onSuccess: () => {
      setForm(null);
      setFormError(undefined);
      invalidate();
    },
    onError: (e) => setFormError(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: () => CustomFieldsApi.remove(deleting!.id),
    onSuccess: () => {
      setDeleting(null);
      setDeleteError(undefined);
      invalidate();
    },
    onError: (e) => setDeleteError(errMsg(e)),
  });

  const restore = useMutation({
    mutationFn: (id: string) => CustomFieldsApi.restore(id),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => CustomFieldsApi.reorder(ids),
    onSuccess: invalidate,
  });

  const move = (field: CustomField, direction: -1 | 1) => {
    const list = fields ?? [];
    const idx = list.findIndex((f) => f.id === field.id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= list.length) return;
    const ids = list.map((f) => f.id);
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    reorder.mutate(ids);
  };

  const columns = [
    columnHelper.display({
      id: 'rank',
      header: '',
      cell: (info) =>
        showDeleted ? null : (
          <div className="flex flex-col">
            <button
              type="button"
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
              disabled={info.row.index === 0}
              onClick={() => move(info.row.original, -1)}
              title="Move up"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
              disabled={info.row.index === (fields?.length ?? 0) - 1}
              onClick={() => move(info.row.original, 1)}
              title="Move down"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
    }),
    columnHelper.accessor('name', {
      header: 'Field',
      cell: (info) => <span className="font-medium text-slate-900">{info.getValue()}</span>,
    }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: (info) => <Badge tone="slate">{CUSTOM_FIELD_TYPE_LABELS[info.getValue()]}</Badge>,
    }),
    columnHelper.accessor('isRequired', {
      header: 'Required',
      cell: (info) => (info.getValue() ? <Badge tone="amber">Required</Badge> : <span className="text-slate-400">Optional</span>),
    }),
    columnHelper.accessor('options', {
      header: 'Options',
      cell: (info) => {
        const opts = info.getValue();
        if (!opts?.length) return <span className="text-slate-400">—</span>;
        return <span className="text-sm text-slate-600">{opts.join(', ')}</span>;
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <div className="flex items-center gap-1">
          {showDeleted ? (
            <button
              type="button"
              title="Restore"
              className="rounded p-1 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600"
              onClick={() => restore.mutate(info.row.original.id)}
            >
              <Undo2 className="h-4 w-4" />
            </button>
          ) : (
            <>
              <button
                type="button"
                title="Edit"
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                onClick={() => {
                  setFormError(undefined);
                  setForm({
                    id: info.row.original.id,
                    name: info.row.original.name,
                    type: info.row.original.type,
                    isRequired: info.row.original.isRequired,
                    options: info.row.original.options ?? [],
                  });
                }}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Delete"
                className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => {
                  setDeleteError(undefined);
                  setDeleting(info.row.original);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      ),
    }),
  ];

  const showOptions = form ? OPTION_TYPES.has(form.type) : false;

  return (
    <div>
      <PageHeader
        title="Custom Fields"
        description="Extra admission-form fields captured for every student (e.g. Blood Group, Father's Mobile, Allergies) — appear on the student admission form in this order."
        actions={
          !showDeleted && (
            <button
              type="button"
              className="btn-primary flex items-center gap-1.5"
              onClick={() => {
                setFormError(undefined);
                setForm({ id: null, name: '', type: 'text', isRequired: false, options: [] });
              }}
            >
              <Plus className="h-4 w-4" /> Add Field
            </button>
          )
        }
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{errMsg(error)}</div>
      ) : (
        <DataTable
          columns={columns}
          result={asStaticPage(fields ?? [])}
          state={dt}
          loading={isFetching}
          emptyMessage={showDeleted ? 'No deleted fields.' : 'No custom fields set up yet.'}
          toolbar={
            <div className="flex w-full flex-wrap items-center gap-3">
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={dt.searchInput}
                  onChange={(e) => dt.setSearchInput(e.target.value)}
                  placeholder="Search field name…"
                  className="pl-9"
                />
              </div>
              <label className="flex items-center gap-1.5 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                Show deleted
              </label>
            </div>
          }
        />
      )}

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Edit Custom Field' : 'Add Custom Field'}
        size="sm"
      >
        {form && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
            className="space-y-4"
          >
            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</div>
            )}
            <Field label="Name" required>
              <Input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Blood Group"
                required
              />
            </Field>
            <Field label="Type" required hint={form.id ? "Can't be changed after creation" : undefined}>
              <Select
                value={form.type}
                disabled={!!form.id}
                onChange={(e) => setForm({ ...form, type: e.target.value as CustomFieldType })}
              >
                {CUSTOM_FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {CUSTOM_FIELD_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <label className="flex items-center gap-1.5 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.isRequired}
                onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
              />
              Required on the admission form
            </label>

            {showOptions && (
              <Field label="Options" required hint="Shown as the choices for this field">
                <div className="space-y-2">
                  {form.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={opt}
                        onChange={(e) => {
                          const options = [...form.options];
                          options[i] = e.target.value;
                          setForm({ ...form, options });
                        }}
                        placeholder={`Option ${i + 1}`}
                      />
                      <button
                        type="button"
                        className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="text-sm font-medium text-brand-600 hover:underline"
                    onClick={() => setForm({ ...form, options: [...form.options, ''] })}
                  >
                    + Add option
                  </button>
                </div>
              </Field>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setForm(null)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  !form.name.trim() ||
                  save.isPending ||
                  (showOptions && form.options.map((o) => o.trim()).filter(Boolean).length === 0)
                }
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
        title="Delete field?"
        message={
          deleteError ??
          `This will remove "${deleting?.name}" from the admission form. Previously captured values are kept — you can restore it later from "Show deleted".`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}
